import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightFromBracket,
  faBars,
  faCircleHalfStroke,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";

import Profile from "../profile/Profile";
import "./HeaderNav.scss";
import DropdownMenu from "../dropdown-menu/DropdownMenu";
import {
  bywatersFacebook,
  bywatersInstagram,
  bywatersLinkedin,
  linkLabels,
} from "../../service/utils/constants";
import { logout } from "../../service/graphql/utils";
import { checkPagePermissions } from "../../service/utils/functions";
import { PagePermissionMask } from "../../service/utils/interfaces";

interface HeaderNavProps {
  name: string;
  darkMode: boolean;
  onThemeSwitch: () => void;
  screenWidth: number;
  screenHeight: number;
  permMask?: PagePermissionMask;
  isTippingUser: boolean;
}

const HeaderNav = (props: HeaderNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [locationName, setLocationName] = useState("");
  const [isDropdownLogoutOpen, setIsDropdownLogoutOpen] = useState(false);
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const locationPath = linkLabels.find(
      (elem) => elem.path === location.pathname
    );
    if (locationPath) setLocationName(locationPath.name);
    else
      switch (location.pathname) {
        case "/dashboard/help/elearning":
          setLocationName("E-Learning");
          break;
        case "/dashboard/help/contact-us":
          setLocationName("Contact Us");
      }
  }, [location]);

  const toggleDropdownLogoutBox = () => {
    if (isDropdownLogoutOpen) setClosing(true);
    else setIsDropdownLogoutOpen(true);
  };

  const toggleDropdownMenuBox = () => {
    if (isDropdownMenuOpen) setClosing(true);
    else setIsDropdownMenuOpen(true);
  };

  useEffect(() => {
    if (closing) {
      setTimeout(() => {
        setIsDropdownLogoutOpen(false);
        setIsDropdownMenuOpen(false);
        setClosing(false);
      }, 175);
    }
  }, [closing]);

  const onLogoutAction = () => {
    toggleDropdownLogoutBox();
    logout(navigate);
  };

  return (
    <>
      <div className={"header" + (props.darkMode ? " dark" : "")}>
        <img
          className={"logo" + (props.darkMode ? " dark" : "")}
          src={
            props.darkMode
              ? "/images/bywaters_white_logo.png"
              : "/images/bywaters_logo.png"
          }
          alt="bywaters_logo"
        />
        <div className="header-content">
          <div className="header-line">
            <div className="socials-subrow">
              <div className="social-icon-wrapper">
                <a
                  title="linkedin"
                  href={bywatersLinkedin}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon
                    className={"social-icon" + (props.darkMode ? " dark" : "")}
                    icon={faLinkedin}
                  />
                </a>
              </div>
              <div className="social-icon-wrapper">
                <a
                  title="facebook"
                  href={bywatersFacebook}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon
                    className={"social-icon" + (props.darkMode ? " dark" : "")}
                    icon={faFacebook}
                  />
                </a>
              </div>
              <div className="social-icon-wrapper">
                <a
                  title="facebook"
                  href={bywatersInstagram}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon
                    className={"social-icon" + (props.darkMode ? " dark" : "")}
                    icon={faInstagram}
                  />
                </a>
              </div>
            </div>
            <Profile
              name={props.name}
              onClick={toggleDropdownLogoutBox}
              screenWidth={props.screenWidth}
            />
          </div>
          <div className="header-line">
            <div className="dashboard-path">
              <span>{"Dashboard / " + locationName}</span>
            </div>
            <div
              className="theme-switcher"
              onClick={() => props.onThemeSwitch()}
            >
              <FontAwesomeIcon
                className="theme-icon"
                icon={faCircleHalfStroke}
              />
              <span>{(props.darkMode ? "Dark" : "Light") + " Mode"}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={"header-mobile" + (props.darkMode ? " dark" : "")}>
        <FontAwesomeIcon
          className={"menu-icon" + (props.darkMode ? " dark" : "")}
          icon={faBars}
          onClick={toggleDropdownMenuBox}
        />
      </div>
      {isDropdownMenuOpen && (
        <>
          <div
            className="context-menu-overlay"
            onClick={toggleDropdownMenuBox}
          ></div>
          <div className="dropdown-menu-box">
            <DropdownMenu closing={closing}>
              <div
                className={"navigation-links" + (props.darkMode ? " dark" : "")}
                style={{ visibility: props.permMask ? "visible" : "hidden" }}
              >
                {linkLabels.map((link, idx) => {
                  const isVisible = checkPagePermissions(
                    link.name,
                    props.isTippingUser,
                    props.permMask
                  );
                  return (
                    <div
                      key={"dashboard-link-" + idx}
                      style={{ display: isVisible ? "block" : "none" }}
                    >
                      {!link.isInternal ? (
                        <a
                          className="link-item"
                          href={link.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={toggleDropdownMenuBox}
                        >
                          <FontAwesomeIcon
                            className={
                              "sidenav-icon" + (props.darkMode ? " dark" : "")
                            }
                            icon={link.icon}
                          />
                          <span
                            className={
                              "link-name" + (props.darkMode ? " dark" : "")
                            }
                          >
                            {link.name === "Job List & Issues" &&
                            props.isTippingUser
                              ? "Job List"
                              : link.name}
                          </span>
                        </a>
                      ) : (
                        <NavLink
                          className={({ isActive }) =>
                            "link-item" + (isActive ? " active" : "")
                          }
                          to={link.path}
                          onClick={toggleDropdownMenuBox}
                        >
                          <FontAwesomeIcon
                            className={
                              "sidenav-icon" + (props.darkMode ? " dark" : "")
                            }
                            icon={link.icon}
                          />
                          <span
                            className={
                              "link-name" + (props.darkMode ? " dark" : "")
                            }
                          >
                            {link.name === "Job List & Issues" &&
                            props.isTippingUser
                              ? "Job List"
                              : link.name}
                          </span>
                        </NavLink>
                      )}
                    </div>
                  );
                })}
              </div>
            </DropdownMenu>
          </div>
        </>
      )}
      {isDropdownLogoutOpen && (
        <>
          <div
            className="context-menu-overlay"
            onClick={toggleDropdownLogoutBox}
          ></div>
          <div className="dropdown-box">
            <DropdownMenu closing={closing}>
              <div
                className={"logout-btn" + (props.darkMode ? " dark" : "")}
                onClick={onLogoutAction}
              >
                <FontAwesomeIcon
                  className="icon"
                  icon={faArrowRightFromBracket}
                />
                <p>Logout</p>
              </div>
            </DropdownMenu>
          </div>
        </>
      )}
    </>
  );
};

export default HeaderNav;
